import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { logout } from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

export const fetchTableOne = createAsyncThunk(
  "dashFrame/fetchTableOne",
  async ({ liverun, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_count_by_week`,
        { liverun, license_key },
      );

      return res.data || {};
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchTableTwo = createAsyncThunk(
  "dashFrame/fetchTableTwo",
  async (
    { start_date, end_date, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_category_by_week`,
        { start_date, end_date, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchTableThree = createAsyncThunk(
  "dashFrame/fetchTableThree",
  async (
    { start_date, end_date, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_week_by_date`,
        { start_date, end_date, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchDayByCategory = createAsyncThunk(
  "dashFrame/fetchDayByCategory",
  async ({ date, liverun, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_category_by_date`,
        { date, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchDayData = createAsyncThunk(
  "dashFrame/fetchDayData",
  async ({ date, liverun, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/blood_filings_data_by_date`,
        { date, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

export const fetchFiling = createAsyncThunk(
  "dashFrame/fetchFiling",
  async (
    { lab_report_id, liverun, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/emis_pathology/api/fetch_blood_filing`,
        { lab_report_id, liverun, license_key },
      );
      return res.data || [];
    } catch (error) {
      if (error.response && error.response.status === 403) {
        dispatch(logout());
      }
      return rejectWithValue(error.response?.data || "Something went wrong");
    }
  },
);

// === SLICE ===

const dashFrameSlice = createSlice({
  name: "dashFrame",
  initialState: {
    liverun: true,

    tableOne: [],
    tableOneTotalProcessed: 0,
    tableOneTotalNormal: 0,
    tableOneTotalAbnormal: 0,
    tableOneInreviewCategories: 0,
    tableOneEnabledCategories: 0,
    tableOneMonthlyTrend: [],

    tableTwo: [],
    tableThree: [],
    tableDayByCategory: [],
    tableDayData: [],
    lab: {},
    selectedDate: null,
    selectedDay: null,
    selectedLabReport: null,
    loadingOne: false,
    loadingTwo: false,
    loadingThree: false,
    loadingDayByCategory: false,
    loadingDayData: false,
    loadingLabReport: false,
  },
  reducers: {
    toggleLiverun(state) {
      state.liverun = !state.liverun;
      state.tableTwo = [];
      state.tableThree = [];
      state.selectedDate = null;
    },
    setSelectedDate(state, action) {
      state.selectedDate = action.payload;
    },
    setSelectedDay(state, action) {
      state.selectedDay = action.payload;
    },
    setLabReport(state, action) {
      state.selectedLabReport = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Table One
      .addCase(fetchTableOne.pending, (state) => {
        state.loadingOne = true;
      })
      .addCase(fetchTableOne.fulfilled, (state, action) => {
        const {
          count_by_week = [],
          total_processed = 0,
          total_normal = 0,
          total_abnormal = 0,
          inreview_categories = 0,
          enabled_categories = 0,
          result = [],
        } = action.payload;

        state.tableOne = count_by_week.map((rec, idx) => ({
          id: idx,
          date: rec[0],
          start_date: rec[1],
          end_date: rec[2],
          filed: rec[4],
          reviewed_and_actioned: rec[5],
          not_filed_inconclusive: rec[6],
          not_filed_setup: rec[7],
          not_filed_already: rec[8],
          error: rec[9],
        }));

        state.tableOneTotalProcessed = total_processed;
        state.tableOneTotalNormal = total_normal;
        state.tableOneTotalAbnormal = total_abnormal;
        state.tableOneInreviewCategories = inreview_categories;
        state.tableOneEnabledCategories = enabled_categories;
        state.tableOneMonthlyTrend = result;

        state.loadingOne = false;
      })

      // Table Two
      .addCase(fetchTableTwo.pending, (state) => {
        state.loadingTwo = true;
      })
      .addCase(fetchTableTwo.fulfilled, (state, action) => {
        state.tableTwo = action.payload.map((rec, idx) => ({
          id: idx,
          category: rec[0],
          filed: rec[2],
          reviewed_and_actioned: rec[3],
          not_filed_inconclusive: rec[4],
          not_filed_setup: rec[5],
          not_filed_already: rec[6],
          error: rec[7],
        }));
        state.loadingTwo = false;
      })

      // Table Three
      .addCase(fetchTableThree.pending, (state) => {
        state.loadingThree = true;
      })
      .addCase(fetchTableThree.fulfilled, (state, action) => {
        state.tableThree = action.payload.map((rec, idx) => ({
          id: idx,
          output_date: rec[0],
          pathology_enabled: rec[13],
          enabled_received: rec[2],
          filed: rec[3],
          per_filed: rec[4],
          filed_abnormal: rec[5],
          per_filed_abnormal: rec[6],
          reviewed_and_actioned: rec[7],
          percentage_reviewed_and_actioned: rec[8],
          filed_by_clinician: rec[9],
          per_filed_by_clinician: rec[10],
          not_setup_count: rec[11],
          inconclusive_report: rec[12],
          inreview: rec[16],
        }));
        state.loadingThree = false;
      })

      // Day By Category
      .addCase(fetchDayByCategory.pending, (state) => {
        state.loadingDayByCategory = true;
      })
      .addCase(fetchDayByCategory.fulfilled, (state, action) => {
        state.tableDayByCategory = action.payload.map((rec, idx) => ({
          id: idx,
          category: rec[0],
          filed: rec[2],
          reviewed_and_actioned: rec[3],
          not_filed_inconclusive: rec[4],
          not_filed_setup: rec[5],
          not_filed_already: rec[6],
          error: rec[7],
        }));
        state.loadingDayByCategory = false;
      })

      // Day Data
      .addCase(fetchDayData.pending, (state) => {
        state.loadingDayData = true;
      })
      .addCase(fetchDayData.fulfilled, (state, action) => {
        state.tableDayData = action.payload.map((rec, idx) => ({
          id: idx,
          emis_id: rec[0],
          lab_report_id: rec[1],
          category: rec[2],
          category_type: rec[3],
          status: rec[4],
          starttime: rec[5],
          notes: rec[6],
        }));
        state.loadingDayData = false;
      })
      .addCase(fetchFiling.pending, (state) => {
        state.loadingLabReport = true;
      })
      .addCase(fetchFiling.fulfilled, (state, action) => {
        const report = action.payload;

        state.lab = {
          license_email: report.license_email || "",
          approved: report.approved ?? null,
          updated_at: report.updated_at || "",
          updated_by: report.updated_by || "",
          patient_data: report.patient_data || {},
          patient_profile: report.patient_profile || {},
          categories: Array.isArray(report.categories) ? report.categories : [],
          actions: Array.isArray(report.actions) ? report.actions : [],
        };
        state.loadingLabReport = false;
      })
      .addCase(fetchFiling.rejected, (state) => {
        state.loadingLabReport = false;
      });
  },
});

export const { toggleLiverun, setSelectedDate, setSelectedDay, setLabReport } =
  dashFrameSlice.actions;

export default dashFrameSlice.reducer;
